import { ErrorType, EveError, EveRedirect, EveRoutes } from '@ver-uds/e-verify';
import { EverifyUserRole, MyI9TrainingStatus, User } from '@vis-auth/vis-user-client';
import { Redirect, Route, Switch } from 'react-router-dom';

import React from 'react';
import ContactUs from './components/ContactUs/ContactUs';
import Environment from './constants/Environment';
import Feature from './constants/Feature';
import Home from './components/Home/Home';
import I9Training from './components/I9Training/I9Training';
import I9TermsOfService from './components/I9TermsOfService/I9TermsOfService';
import InternalLinks from './constants/InternalLinks';
import MessageCenter from './components/MessageCenter/MessageCenter';
import PageContainer from './components/PageContainer/PageContainer';
import SiteMap from './components/SiteMap/SiteMap';
import ViewEssentialResources from './components/ViewEssentialResources/ViewEssentialResources';
import ViewMyI9Training from './components/ViewMyI9Training/ViewMyI9Training';
import { useAccount } from './context/Account/AccountContext';
import { useEnvironment } from './context/Environment/EnvironmentContext';
import { useToggles } from './context/Toggles/TogglesContext';
import { useUser } from './context/User/UserContext';
import MainRoute from './components/MainRoute/MainRoute';
import I9Constants from './components/I9Constants/I9Constants';

const PageRoutes = (): JSX.Element => {
  const { ENVIRONMENT_NAME } = useEnvironment();

  const { myI9TrainingStatus, tutorialComplete, roles } = useUser() as User;
  const evePlusTrainingComplete =
    myI9TrainingStatus === MyI9TrainingStatus.COMPLETE || myI9TrainingStatus === MyI9TrainingStatus.OVERRIDDEN;
  const { myI9, isEvePlusEligible } = useAccount();
  const evePlusEnabled = myI9?.enabled ?? false;
  const showTraining: boolean = evePlusTrainingComplete && evePlusEnabled;
  const showEvePlusAttestation = evePlusEnabled && !evePlusTrainingComplete;
  const showTutorial = !tutorialComplete && !roles?.includes(EverifyUserRole.ENROLLMENT);
  const showEvePlusToS = isEvePlusEligible && !evePlusTrainingComplete;

  const toggles = useToggles();
  const nawToAawToggle = toggles?.get('CITIZENSHIP_NONCITIZEN_AUTHORIZED_TO_WORK') === 'on' ? 'noncitizen' : 'alien';
  const isMfaMandatory = toggles?.get('MFA_MANDATORY') === 'on';

  const user = useUser() as User;
  const forceMigratePage = isMfaMandatory && !user?.mfaEnabled;

  return (
    <Switch>
      {forceMigratePage && <Redirect to="/migrate" />}

      <MainRoute
        exact
        path={EveRoutes.HOME}
        showTutorial={showTutorial}
        showEvePlusAttestation={showEvePlusAttestation}
      >
        <Home />
      </MainRoute>

      <MainRoute
        exact
        path={EveRoutes.RESOURCES_CONTACT_US}
        showTutorial={showTutorial}
        showEvePlusAttestation={showEvePlusAttestation}
      >
        <PageContainer
          title="Contact Us"
          header="Contact Us"
          content={Feature.FEATURE_CONTACT_US}
          showTraining={showTraining}
        >
          <ContactUs />
        </PageContainer>
      </MainRoute>

      <MainRoute
        exact
        path={EveRoutes.RESOURCES_NEWS}
        showTutorial={showTutorial}
        showEvePlusAttestation={showEvePlusAttestation}
      >
        <PageContainer title="News" header="Resources" content={Feature.FEATURE_NEWS} showTraining={showTraining}>
          <MessageCenter />
        </PageContainer>
      </MainRoute>

      <MainRoute
        exact
        path={EveRoutes.RESOURCES_VIEW}
        showTutorial={showTutorial}
        showEvePlusAttestation={showEvePlusAttestation}
      >
        <PageContainer
          title="View Essential Resources"
          header="Resources"
          content={Feature.FEATURE_VIEW_RESOURCES}
          showTraining={showTraining}
        >
          <ViewEssentialResources />
        </PageContainer>
      </MainRoute>

      <MainRoute
        exact
        path={EveRoutes.RESOURCES_VIEW_TRAINING}
        showTutorial={showTutorial}
        showEvePlusAttestation={showEvePlusAttestation}
        conditionToShow={showTraining}
      >
        <PageContainer
          title={`View ${I9Constants.PROGRAM_NAME} Training`}
          header="Resources"
          content={Feature.FEATURE_VIEW_TRAINING}
          showTraining={showTraining}
        >
          <ViewMyI9Training />
        </PageContainer>
      </MainRoute>

      <MainRoute
        exact
        path={EveRoutes.SITEMAP}
        showTutorial={showTutorial}
        showEvePlusAttestation={showEvePlusAttestation}
      >
        <SiteMap />
      </MainRoute>

      <MainRoute
        exact
        path={EveRoutes.I9_TRAINING}
        showTutorial={showTutorial}
        showEvePlusAttestation={showEvePlusAttestation}
        conditionToShow={showEvePlusAttestation}
      >
        <I9Training />
      </MainRoute>

      <MainRoute
        exact
        path={EveRoutes.EVERIFY_PLUS_TOS}
        showTutorial={showTutorial}
        showEvePlusAttestation={showEvePlusAttestation}
        conditionToShow={showEvePlusToS}
      >
        <I9TermsOfService nawToAawToggle={nawToAawToggle} />
      </MainRoute>

      <Route path={EveRoutes.LOGIN_DOT_GOV_LOGIN}>
        <Redirect to={EveRoutes.DASHBOARD} />
      </Route>

      <Route path={InternalLinks.PAGE_NOT_FOUND}>
        <EveError errorType={ErrorType.NOT_FOUND} />
      </Route>

      {ENVIRONMENT_NAME === Environment.LOCAL ? (
        <Redirect to={InternalLinks.PAGE_NOT_FOUND} />
      ) : (
        <EveRedirect pageNotFoundPath={InternalLinks.PAGE_NOT_FOUND} />
      )}
    </Switch>
  );
};

export default PageRoutes;
